import { rewardLabel, useTaskCards, type TaskCard } from '@/ui/hooks/useTaskCards';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useServices } from '@/ui/services/ServicesContext';
import shell from './CatalogSheet.module.css';
import styles from './TasksSheet.module.css';
import { useBack } from '@/ui/back/backStack';
import { Icon } from '@/ui/components/Icon';
import { SpotArt } from '@/ui/art/SpotArt';
import { NeighborhoodCards } from './NeighborhoodCards';

/**
 * S08 Gợi ý (ux-screens §3.8): tutorial step during FTUE, then up to 3 gentle suggestions. No timers, no fail.
 * "Chỉ chỗ" closes the sheet so the coach spotlight shows the spot; "Đi tới" goes where the task is done.
 */
export function TasksSheet({
  onClose,
  onGoTo,
  onShowMe,
}: {
  onClose: () => void;
  onGoTo: (goto: NonNullable<TaskCard['goto']>) => void;
  onShowMe: (highlight: TaskCard['highlight']) => void;
}) {
  const { t } = useI18n();
  const { dispatch } = useServices();
  const { cards, inTutorial, tutorialTotal } = useTaskCards();
  useBack(onClose);

  const claim = (c: TaskCard) => {
    dispatch({
      type: 'claimReward',
      source: { kind: 'task', taskId: c.taskId, ...(c.instanceKey ? { instanceKey: c.instanceKey } : {}) },
    });
  };
  const swap = (c: TaskCard) => {
    dispatch({
      type: 'swapSuggestion',
      taskId: c.taskId,
      ...(c.instanceKey ? { instanceKey: c.instanceKey } : {}),
    });
  };

  return (
    <div
      className={shell.scrim}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section className={shell.sheet} role="dialog" aria-modal="true" aria-label={t('ui.tasks.title')}>
        <div className={shell.grip} aria-hidden="true" />
        <header className={shell.header}>
          <h2 className={shell.title}>{t('ui.tasks.title')}</h2>
          <button
            type="button"
            className={shell.iconBtn}
            aria-label={t('ui.catalog.close')}
            onClick={onClose}
          >
            <Icon name="close" size={26} />
          </button>
        </header>
        <p className={styles.sub}>{t('ui.tasks.subtitle')}</p>
        <ul className={styles.list} data-testid="tasks-list">
          {!inTutorial && <NeighborhoodCards onGoTo={onGoTo} />}
          {cards.length === 0 && (
            <li className={styles.empty}>
              <SpotArt name="empty_tasks" fallback="ideas" size={130} />
              <p>{t('ui.tasks.empty')}</p>
            </li>
          )}
          {cards.map((c) => {
            const done = c.status === 'completed';
            const pct = Math.round((Math.min(c.current, c.target) / Math.max(1, c.target)) * 100);
            return (
              <li
                key={c.instanceKey ?? c.taskId}
                className={styles.card}
                data-done={done || undefined}
                data-kind={c.kind}
              >
                {inTutorial && (
                  <span className={styles.step}>
                    {t('ui.tasks.tutorial_step', { step: c.step ?? 1, total: tutorialTotal })}
                  </span>
                )}
                <div className={styles.row}>
                  <span className={styles.name}>
                    {t(c.titleKey)}
                    {done && (
                      <span role="img" aria-label={t('ui.tasks.done')} className={styles.done}>
                        <Icon name="check" size={18} />
                      </span>
                    )}
                  </span>
                  <span className={styles.reward}>{rewardLabel(c.rewards, t)}</span>
                </div>
                {c.hintKey && <p className={styles.hint}>{t(c.hintKey, c.hintParams)}</p>}
                {c.target > 1 && (
                  <div className={styles.progress}>
                    <div
                      className={styles.bar}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={c.target}
                      aria-valuenow={Math.min(c.current, c.target)}
                    >
                      <span style={{ width: `${String(pct)}%` }} />
                    </div>
                    <span className={styles.count}>
                      {t('ui.tasks.progress', { current: Math.min(c.current, c.target), target: c.target })}
                    </span>
                  </div>
                )}
                {c.kind === 'tutorial' && !done && (
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.go}
                      data-testid={`task-show-${c.taskId}`}
                      onClick={() => {
                        onShowMe(c.highlight);
                      }}
                    >
                      {t('ui.tasks.show_me')}
                    </button>
                  </div>
                )}
                {c.kind !== 'tutorial' && !done && c.canSwap === false && (
                  <p className={styles.hint}>{t('ui.tasks.no_swap')}</p>
                )}
                {c.kind !== 'tutorial' && (
                  <div className={styles.actions}>
                    {done ? (
                      <button
                        type="button"
                        className={styles.claim}
                        data-testid={`task-claim-${c.taskId}`}
                        onClick={() => {
                          claim(c);
                        }}
                      >
                        {t('ui.tasks.claim', { reward: rewardLabel(c.rewards, t) })}
                      </button>
                    ) : (
                      <>
                        {c.canSwap !== false && (
                          <button
                            type="button"
                            className={styles.swap}
                            onClick={() => {
                              swap(c);
                            }}
                          >
                            {t('ui.tasks.swap')}
                          </button>
                        )}
                        {c.goto && (
                          <button
                            type="button"
                            className={styles.go}
                            data-testid={`task-go-${c.taskId}`}
                            onClick={() => {
                              if (c.goto) onGoTo(c.goto);
                            }}
                          >
                            {t('ui.tasks.go_to')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
